import { forwardRef, type ReactNode } from "react";
import { Box, Badge, Stack, Button, Flex } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import ProfileCollapsibleSection from "@/components/shared/ProfileCollapsibleSection";
import type { Question, Answer, Section } from "@/utils/gradingEngine.types";
import AssessmentQuestionField from "./AssessmentQuestionField";
import { ASSESSMENT_SECTION_COLLAPSE_MS } from "./assessmentSection.const";

const BRAND = "var(--mantine-color-primary-6)";

function displaySectionTitle(title: string): string {
  return title.replace(/^[A-Z]\.\s*/, "");
}

type Props = {
  section: Section;
  sectionIndex: number;
  questions: Question[];
  answers: Record<string, Answer>;
  opened: boolean;
  onOpenedChange: (opened: boolean) => void;
  answeredCount: number;
  isLastSection: boolean;
  readOnly?: boolean;
  submitting?: boolean;
  canSubmit?: boolean;
  onChange: (answer: Answer) => void;
  onClear: (questionId: string) => void;
  onNext: () => void;
  onSubmit: () => void;
};

const AssessmentSectionCard = forwardRef<HTMLDivElement, Props>(function AssessmentSectionCard(
  {
    section,
    sectionIndex,
    questions,
    answers,
    opened,
    onOpenedChange,
    answeredCount,
    isLastSection,
    readOnly = false,
    submitting = false,
    canSubmit = false,
    onChange,
    onClear,
    onNext,
    onSubmit,
  },
  ref
) {
  const total = questions.length;
  const pct = total > 0 ? Math.round((answeredCount / total) * 100) : 0;

  const sectionIcon: ReactNode = (
    <Box
      style={{
        width: 28,
        height: 28,
        minWidth: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: 13,
        border: "1.5px solid #ffc300",
        backgroundColor: opened ? "#ffc300" : "transparent",
        color: opened ? "#04305c" : "#ffc300",
      }}
    >
      {sectionIndex + 1}
    </Box>
  );

  return (
    <Box ref={ref} style={{ scrollMarginTop: 12 }}>
      <ProfileCollapsibleSection
        title={displaySectionTitle(section.title)}
        icon={sectionIcon}
        opened={opened}
        onOpenedChange={onOpenedChange}
        unmountOnCollapse
        collapseDurationMs={ASSESSMENT_SECTION_COLLAPSE_MS}
        mb="md"
        headerRight={
          <Badge
            variant="light"
            color="yellow"
            size="sm"
            styles={{ root: { color: "#ffc300", fontWeight: 600 } }}
          >
            {answeredCount}/{total} · {pct}%
          </Badge>
        }
      >
        <Stack gap={0}>
          {questions.map((question, qIdx) => (
            <AssessmentQuestionField
              key={question.id}
              question={question}
              answer={answers[question.id]}
              readOnly={readOnly}
              onChange={onChange}
              onClear={() => onClear(question.id)}
              showDivider={qIdx > 0}
            />
          ))}
        </Stack>

        <Flex justify="flex-end" mt="md" pt="sm">
          {isLastSection ? (
            <Button
              color="primary"
              size="sm"
              rightSection={<IconArrowRight size={16} />}
              onClick={onSubmit}
              loading={submitting}
              disabled={readOnly || !canSubmit}
              styles={{ root: { backgroundColor: BRAND } }}
            >
              Submit Assessment
            </Button>
          ) : (
            <Button
              color="primary"
              size="sm"
              rightSection={<IconArrowRight size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              disabled={readOnly}
              styles={{ root: { backgroundColor: BRAND } }}
            >
              Next
            </Button>
          )}
        </Flex>
      </ProfileCollapsibleSection>
    </Box>
  );
});

export default AssessmentSectionCard;
